import React, { useState, useEffect } from 'react';
import marksAPI from '../api/marksAPI';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdFilterList,
  MdDownload,
  MdSchool,
  MdPeople,
  MdSchedule,
  MdBook,
  MdDescription,
  MdViewModule,
  MdViewList,
  MdPerson,
  MdPhone,
  MdEmail,
  MdArrowForward
} from 'react-icons/md';
import jsPDF from 'jspdf';
// Import autoTable plugin - for v5.x, use default import
import autoTable from 'jspdf-autotable';
// Import university logo
import universityLogo from '../../assets/images/Benadir University Logo.png';

/**
 * Teacher Sheet Report Component this sheet
 * Filter students by Faculty, Department, and Batch
 * Display filtered student list and allow PDF download in exam-style format
 */
function TeacherSheetReport() {
  const { getToken, user } = useAttendanceAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [sheetDetails, setSheetDetails] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'box'
  
  // Filter options
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  // Selected filters
  const [filters, setFilters] = useState({
    faculty_id: '',
    department_id: '',
    batch_id: ''
  });

  useEffect(() => {
    // Test API connection first
    const testAPI = async () => {
      try {
        const token = getToken();
        console.log('🧪 Testing Marks API connection...');
        console.log('🧪 Base URL:', marksAPI.baseURL || '(using proxy)');
        console.log('🧪 Full ping URL:', `${marksAPI.baseURL || ''}/api/marks/ping`);
        console.log('🧪 Has token:', !!token);
        
        const pingResult = await marksAPI.ping(token);
        console.log('✅ Marks API ping result:', pingResult);
      } catch (err) {
        console.error('❌ Marks API ping failed:', err);
        console.error('❌ Error details:', {
          message: err.message,
          stack: err.stack
        });
        
        // Show user-friendly error
        if (err.message.includes('endpoint not found') || err.message.includes('404')) {
          setError(`⚠️ Marks API is not accessible.\n\n` +
            `The backend endpoints may not be registered or the server needs to be restarted.\n\n` +
            `Please verify:\n` +
            `1. The backend server is running\n` +
            `2. The routes are registered in the backend\n` +
            `3. The server has been restarted after implementing the endpoints\n\n` +
            `Implementation location: Shaag/openeducat_attendance/controllers/marks_api.py`);
        }
      }
    };
    
    testAPI();
    loadFilterOptions();
  }, []);

  // Debug: Log when filters change
  useEffect(() => {
    console.log('Current filters:', filters);
    console.log('Faculties:', faculties);
    console.log('Departments:', departments);
    console.log('Batches:', batches);
    console.log('Subjects:', subjects);
  }, [filters, faculties, departments, batches, subjects]);

  useEffect(() => {
    // Load departments when faculty changes
    if (filters.faculty_id) {
      loadDepartments(filters.faculty_id);
    } else {
      setDepartments([]);
      setFilters(prev => ({ ...prev, department_id: '' }));
    }
  }, [filters.faculty_id]);

  useEffect(() => {
    // Load batches when faculty and department are selected
    if (filters.faculty_id && filters.department_id) {
      loadBatches();
    } else {
      setBatches([]);
      setFilters(prev => ({ ...prev, batch_id: '' }));
    }
  }, [filters.faculty_id, filters.department_id]);

  useEffect(() => {
    // Auto-load students when batch is selected
    // Load students from the selected batch (subject is optional)
    if (filters.faculty_id && filters.department_id && filters.batch_id) {
      // Small delay to avoid race conditions
      const timer = setTimeout(() => {
        handleSearch();
      }, 100);
      return () => clearTimeout(timer);
    } else if (!filters.batch_id) {
      // Clear students if batch is deselected
      setStudents([]);
      setSheetDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.batch_id, filters.department_id, filters.faculty_id]);

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
      console.log('Loading filter options...');
      console.log('Current user:', user);
      console.log('User faculty info:', user?.faculty);
      
      // For faculty admins, only show their assigned faculty
      if (user?.faculty) {
        const userFaculty = {
          id: user.faculty.id || user.faculty_id,
          name: user.faculty.name || user.faculty_name || 'My Faculty'
        };
        
        console.log('✅ Loading user\'s assigned faculty:', userFaculty);
        setFaculties([userFaculty]);
        
        // Auto-select the user's faculty
        setFilters(prev => ({
          ...prev,
          faculty_id: userFaculty.id.toString()
        }));
        
        console.log('✅ Faculty loaded and auto-selected:', userFaculty.name);
      } else {
        console.warn('⚠️ User has no faculty assigned');
        setFaculties([]);
        setError('⚠️ No faculty assigned to your account.\n\n' +
          'Please contact your administrator to assign a faculty to your account.');
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
      setError(`Failed to load filter options: ${err.message || 'Unknown error'}. Please check the browser console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (facultyId) => {
    try {
      if (!facultyId) {
        setDepartments([]);
        return;
      }
      
      setLoading(true);
      const token = getToken();
      console.log('📋 Loading departments for faculty:', facultyId);
      
      // Try marks API first
      try {
        const deptRes = await marksAPI.getDepartments(parseInt(facultyId), token);
        console.log('📦 Marks API departments response:', deptRes);
        if (deptRes && deptRes.success && deptRes.departments && deptRes.departments.length > 0) {
          const sortedDepts = [...deptRes.departments].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '')
          );
          console.log('✅ Loaded', sortedDepts.length, 'departments from Marks API');
          setDepartments(sortedDepts);
          setLoading(false);
          return;
        }
      } catch (marksErr) {
        console.warn('⚠️ Marks API for departments failed, trying student-list API...', marksErr);
      }
      
      // Fallback: Get departments from student-list API filtered by faculty
      try {
        console.log('📋 Trying to get departments from student-list API filtered by faculty:', facultyId);
        const studentListRes = await marksAPI.getStudentList({ 
          faculty_id: parseInt(facultyId),
          limit: 5000 
        }, token);
        
        if (studentListRes && studentListRes.success && studentListRes.students && studentListRes.students.length > 0) {
          const deptMap = new Map();
          
          studentListRes.students.forEach(student => {
            const deptId = student.department_id;
            const deptName = student.department_name;
            
            // Only include departments that belong to the selected faculty
            if (deptId && student.faculty_id && parseInt(student.faculty_id) === parseInt(facultyId)) {
              if (!deptMap.has(deptId)) {
                deptMap.set(deptId, {
                  id: deptId,
                  name: deptName || `Department ${deptId}`
                });
              } else {
                // Update name if we have a better one
                const existing = deptMap.get(deptId);
                if (deptName && (!existing.name || existing.name.startsWith('Department '))) {
                  existing.name = deptName;
                }
              }
            }
          });
          
          const extractedDepts = Array.from(deptMap.values());
          extractedDepts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          
          console.log('✅ Extracted', extractedDepts.length, 'departments from student-list for faculty:', extractedDepts);
          
          if (extractedDepts.length > 0) {
            setDepartments(extractedDepts);
            setLoading(false);
            return;
          }
        }
      } catch (studentErr) {
        console.warn('⚠️ Could not get departments from student-list:', studentErr);
      }
      
      // Fallback: Get batches and extract departments filtered by faculty
      console.log('📋 Fetching batches to extract departments for faculty:', facultyId);
      const batchesRes = await attendanceAPI.getBatches(token);
      console.log('📦 Batches response for departments:', batchesRes);
      
      if (batchesRes && batchesRes.success && batchesRes.batches && batchesRes.batches.length > 0) {
        const deptMap = new Map();
        
        batchesRes.batches.forEach((batch) => {
          // Check if batch belongs to the selected faculty
          const batchFacultyId = batch.faculty_id || 
                                 batch.faculty?.id || 
                                 (batch.faculty && typeof batch.faculty === 'object' ? batch.faculty.id : null) ||
                                 (batch.faculty && typeof batch.faculty === 'number' ? batch.faculty : null);
          
          // Only process batches that belong to the selected faculty
          if (batchFacultyId && parseInt(batchFacultyId) === parseInt(facultyId)) {
            const deptId = batch.department_id || 
                          batch.department?.id || 
                          (batch.department && typeof batch.department === 'object' ? batch.department.id : null) ||
                          (batch.department && typeof batch.department === 'number' ? batch.department : null);
            
            let deptName = batch.department_name || 
                          batch.department?.name ||
                          (batch.department && typeof batch.department === 'object' ? batch.department.name : null) ||
                          batch.department_code ||
                          batch.department?.code;
            
            if (!deptName && batch.department && typeof batch.department === 'object') {
              deptName = batch.department.name || 
                        batch.department.title || 
                        batch.department.label ||
                        batch.department.full_name ||
                        batch.department.display_name;
            }
            
            if (deptId) {
              if (!deptMap.has(deptId)) {
                deptMap.set(deptId, {
                  id: deptId,
                  name: deptName || `Department ${deptId}`
                });
              } else {
                // Update name if we have a better one
                const existing = deptMap.get(deptId);
                if (deptName && (!existing.name || existing.name.startsWith('Department '))) {
                  existing.name = deptName;
                }
              }
            }
          }
        });
        
        const extractedDepts = Array.from(deptMap.values());
        extractedDepts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        console.log('✅ Extracted', extractedDepts.length, 'departments for faculty:', extractedDepts);
        
        if (extractedDepts.length > 0) {
          setDepartments(extractedDepts);
        } else {
          console.warn('⚠️ No departments found for this faculty');
          setDepartments([]);
        }
      } else {
        console.warn('⚠️ No batches returned or batches response failed');
        setDepartments([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading departments:', err);
      setDepartments([]);
      setError(`Failed to load departments: ${err.message}`);
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      if (!filters.faculty_id || !filters.department_id) {
        setBatches([]);
        return;
      }

      const token = getToken();
      const batchesRes = await attendanceAPI.getBatches({
        faculty_id: parseInt(filters.faculty_id),
        department_id: parseInt(filters.department_id)
      }, token);

      if (batchesRes?.success && Array.isArray(batchesRes.batches)) {
        const batchList = batchesRes.batches.map(batch => ({
          id: batch.id,
          name: batch.name || batch.code || `Batch ${batch.id}`
        }));
        batchList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setBatches(batchList);
      } else {
        setBatches([]);
        setError(batchesRes?.message || 'Failed to load batches for the selected department');
      }
    } catch (err) {
      console.error('Error loading batches:', err);
      setBatches([]);
      setError(err.message || 'Failed to load batches for the selected department');
    }
  };

  const loadSubjects = async () => {
    try {
      const token = getToken();
      const result = await attendanceAPI.getSubjects({
        batch_ids: [parseInt(filters.batch_id)],
        semester: filters.semester
      }, token);
      
      if (result.success) {
        setSubjects(result.subjects || []);
      }
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear dependent filters
    if (field === 'faculty_id') {
      setFilters(prev => ({
        ...prev,
        department_id: '',
        batch_id: ''
      }));
    } else if (field === 'department_id') {
      setFilters(prev => ({
        ...prev,
        batch_id: ''
      }));
    }
  };

  const handleSearch = async () => {
    // Check if required filters are selected (Faculty, Department, Batch)
    if (!filters.faculty_id || !filters.department_id || !filters.batch_id) {
      setError('Please select Faculty, Department, and Batch to load students');
      setStudents([]);
      setSheetDetails(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStudents([]);
      setSheetDetails(null);
      const token = getToken();
      
      console.log('🔍 Getting students from selected batch:', {
        faculty_id: filters.faculty_id,
        department_id: filters.department_id,
        batch_id: filters.batch_id
      });
      
      // Get students from the selected batch
      const studentListParams = {
        batch_id: parseInt(filters.batch_id),
        limit: 1000
      };
      
      console.log('📤 Calling getStudentList with batch_id:', studentListParams);
      const studentListResult = await marksAPI.getStudentList(studentListParams, token);
      console.log('📥 Student list response:', studentListResult);
      console.log('📊 Response success:', studentListResult?.success);
      console.log('📊 Students count:', studentListResult?.students?.length);

      // API returns: { success: true, students: [...], count: ... }
      if (studentListResult && studentListResult.success && studentListResult.students && studentListResult.students.length > 0) {
        console.log('✅ Found', studentListResult.students.length, 'students in batch');
        
        // Convert students to display format
        const studentList = studentListResult.students.map((student, index) => ({
          id: student.id || index,
          student_no: student.student_no || student.student_number || '',
          name: student.name || student.student_name || '',
          phone: student.phone || student.phone_number || student.mobile || '',
          email: student.email || student.email_address || '',
          batch_name: student.batch_name || '',
          faculty_name: student.faculty_name || '',
          department_name: student.department_name || '',
          serial_no: index + 1
        }));
        
        console.log('👥 Processed student list:', studentList);
        setStudents(studentList);
        setLoading(false);
      } else {
        console.warn('⚠️ No students found in batch. Response:', studentListResult);
        if (studentListResult && !studentListResult.success) {
          console.error('❌ API returned success=false:', studentListResult.error || studentListResult.message);
          const errorMsg = studentListResult.error || studentListResult.message || 'Unknown error';
          setError(`Failed to load students: ${errorMsg}`);
        } else {
          setError(`No students found in the selected batch (Batch ID: ${filters.batch_id}).\n\n` +
            `Please ensure the batch has students assigned to it.`);
        }
        setStudents([]);
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error fetching student list:', err);
      console.error('Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      const errorMsg = err.message || 'Failed to fetch student list';
      
      // Check if it's an endpoint not found error
      if (errorMsg.includes('endpoint not found') || errorMsg.includes('not be implemented') || errorMsg.includes('404')) {
        setError(`⚠️ The Marks API endpoint is not available on the server.\n\n` +
          `The backend needs to implement the following endpoint:\n` +
          `POST /api/marks/teacher-sheet/list\n\n` +
          `This endpoint should accept filters (faculty_id, department_id, semester, batch_id, subject_id) and return teacher sheet marks.\n\n` +
          `Please contact your administrator to implement the Marks Management API endpoints.`);
      } else {
        setError(errorMsg);
      }
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTeacherSheet = async () => {
    if (students.length === 0) {
      setError('No students to download');
      return;
    }

    const facultyName = faculties.find(f => f.id === parseInt(filters.faculty_id))?.name || '—';
    const departmentName = departments.find(d => d.id === parseInt(filters.department_id))?.name || '—';
    const batchName = batches.find(b => b.id === parseInt(filters.batch_id))?.name || '—';
    const subjectName = '';
    const sheetDate = new Date()
      .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/\s/g, '-');

    // Keep this PDF identical to the Exam Portal Teacher Sheet Report.
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.width;
    const logoSize = 24;
    const logoTextGap = 8;
    const textWidth = 80;
    const groupStartX = (pageWidth - (logoSize + logoTextGap + textWidth)) / 2;
    const logoX = groupStartX;
    const logoY = 10;
    const textStartX = groupStartX + logoSize + logoTextGap;

    let logoImg = null;
    try {
      logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = universityLogo;
      await new Promise((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error('Logo failed to load'));
        setTimeout(() => reject(new Error('Logo load timeout')), 3000);
      });
    } catch {
      logoImg = null;
    }

    const metaY = 40;
    const leftColX = 14;
    const rightColX = 105;
    const rowHeight = 8;
    const tableWidth = 182;
    const metaHeight = rowHeight * 3;

    const drawHeader = (pageDoc) => {
      if (logoImg) {
        try {
          pageDoc.addImage(logoImg, 'PNG', logoX, logoY, logoSize, logoSize, 'university-logo', 'FAST');
        } catch {
          // Continue without the logo if the browser cannot render it.
        }
      }

      pageDoc.setTextColor(0, 0, 0);
      pageDoc.setFontSize(18);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('BENADIR UNIVERSITY', textStartX, 15);

      pageDoc.setFontSize(9);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text('CULTIVATING HUMAN TALENTS', textStartX, 22);

      pageDoc.setFontSize(14);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Exam Result Sheet', textStartX, 30);

      pageDoc.setDrawColor(0, 0, 0);
      pageDoc.setLineWidth(0.5);
      pageDoc.rect(leftColX, metaY, tableWidth, metaHeight);
      pageDoc.line(leftColX, metaY + rowHeight, leftColX + tableWidth, metaY + rowHeight);
      pageDoc.line(leftColX, metaY + rowHeight * 2, leftColX + tableWidth, metaY + rowHeight * 2);
      pageDoc.line(leftColX + tableWidth / 2, metaY, leftColX + tableWidth / 2, metaY + metaHeight);

      pageDoc.setFontSize(10);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Faculty:', leftColX + 2, metaY + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(facultyName, leftColX + 28, metaY + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Department:', leftColX + 2, metaY + rowHeight + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(departmentName, leftColX + 34, metaY + rowHeight + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Batch:', leftColX + 2, metaY + rowHeight * 2 + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(batchName, leftColX + 24, metaY + rowHeight * 2 + 5.5);

      const rightX = rightColX + 4;
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Subject:', rightX, metaY + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(subjectName, rightX + 26, metaY + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Lecturer Name:', rightX, metaY + rowHeight + 5.5);
      pageDoc.setLineWidth(0.3);
      pageDoc.line(rightX + 36, metaY + rowHeight + 6, rightX + 84, metaY + rowHeight + 6);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Date:', rightX, metaY + rowHeight * 2 + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(sheetDate, rightX + 18, metaY + rowHeight * 2 + 5.5);
    };

    drawHeader(doc);

    autoTable(doc, {
      startY: metaY + metaHeight + 6,
      head: [['S#', 'Student ID', 'Student Name', 'Midterm', 'Final', 'Oral', 'Total']],
      body: students.map((student, index) => [
        student.serial_no || index + 1,
        student.student_no || '',
        student.name || student.student_name || '',
        '',
        '',
        '',
        '',
      ]),
      theme: 'grid',
      showHead: 'everyPage',
      styles: {
        fontSize: 9,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 70 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
      },
      margin: { top: 14, left: 14, right: 14, bottom: 16 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 8,
        { align: 'center' }
      );
    }

    doc.save(
      `Exam_Result_Sheet_${batchName || 'Batch'}_${subjectName || 'Subject'}.pdf`.replace(
        /\s+/g,
        '_'
      )
    );
  };

  const handleDownloadAttendanceSheet = async () => {
    if (students.length === 0) {
      setError('No students to download');
      return;
    }

    // Create PDF in landscape orientation
    const doc = new jsPDF({ orientation: 'landscape', compress: true });
    
    // Get landscape page dimensions
    const landscapePageWidth = doc.internal.pageSize.width;
    const landscapePageHeight = doc.internal.pageSize.height;
    const landscapeMargin = 14;
    
    // Get filter values for header
    const facultyName = faculties.find(f => f.id === parseInt(filters.faculty_id))?.name || 'N/A';
    const departmentName = departments.find(d => d.id === parseInt(filters.department_id))?.name || 'N/A';
    const batchName = batches.find(b => b.id === parseInt(filters.batch_id))?.name || 'N/A';
    const currentDate = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }).replace(/\s/g, '-');
    
    // Calculate center position for logo and text together
    const pageWidth = landscapePageWidth;
    const logoSize = 24;
    const logoTextGap = 8;
    const textWidth = 80;
    const totalWidth = logoSize + logoTextGap + textWidth;
    const groupStartX = (pageWidth - totalWidth) / 2;
    const logoX = groupStartX;
    const logoY = 10;
    const textStartX = groupStartX + logoSize + logoTextGap;
    
    // Load logo image once (will be reused for all pages)
    let logoImg = null;
    try {
      logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = universityLogo;
      
      await new Promise((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error('Logo failed to load'));
        setTimeout(() => reject(new Error('Logo load timeout')), 2000);
      });
    } catch (logoError) {
      console.warn('Could not load logo, will use placeholder:', logoError);
      logoImg = null;
    }
    
    // Define header dimensions (needed both inside drawHeader and for table positioning)
    const infoTableStartY = 40;
    const infoLeftColX = landscapeMargin;
    const infoRightColX = landscapeMargin + (landscapePageWidth - landscapeMargin * 2) / 2;
    const infoRowHeight = 8;
    const infoTableWidth = landscapePageWidth - (landscapeMargin * 2);
    const infoTableHeight = infoRowHeight * 3;
    
    // Helper function to draw header on any page
    const drawHeader = (pageDoc) => {
      // Add logo
      if (logoImg) {
        try {
          pageDoc.addImage(logoImg, 'PNG', logoX, logoY, logoSize, logoSize, 'university-logo', 'FAST');
        } catch (e) {
          // If image can't be added (e.g., on new page), use placeholder
          pageDoc.setFillColor(66, 139, 202);
          pageDoc.circle(logoX + logoSize / 2, logoY + logoSize / 2, 12, 'F');
        }
      } else {
        pageDoc.setFillColor(66, 139, 202);
        pageDoc.circle(logoX + logoSize / 2, logoY + logoSize / 2, 12, 'F');
      }
      
      // University name and tagline (centered with logo)
      const textY1 = 15;
      const textY2 = 22;
      const textY3 = 30;
      
      pageDoc.setFontSize(18);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.setTextColor(0, 0, 0);
      pageDoc.text('BENADIR UNIVERSITY', textStartX, textY1);
      
      pageDoc.setFontSize(9);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text('CULTIVATING HUMAN TALENTS', textStartX, textY2);
      
      // Main title - "Attendance Sheet"
      pageDoc.setFontSize(14);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Attendance Sheet', textStartX, textY3);
      
      // Course/Batch Information Table - Same format as Exam Result Sheet (2 columns, 3 rows)
      
      // Draw info table borders
      pageDoc.setDrawColor(0, 0, 0);
      pageDoc.setLineWidth(0.5);
      pageDoc.rect(infoLeftColX, infoTableStartY, infoTableWidth, infoTableHeight);
      
      // Horizontal lines
      pageDoc.line(infoLeftColX, infoTableStartY + infoRowHeight, infoLeftColX + infoTableWidth, infoTableStartY + infoRowHeight);
      pageDoc.line(infoLeftColX, infoTableStartY + infoRowHeight * 2, infoLeftColX + infoTableWidth, infoTableStartY + infoRowHeight * 2);
      
      // Vertical line (middle separator - 2 columns)
      pageDoc.line(infoLeftColX + infoTableWidth / 2, infoTableStartY, infoLeftColX + infoTableWidth / 2, infoTableStartY + infoTableHeight);
      
      // Fill table content - Left side
      pageDoc.setFontSize(10);
      pageDoc.setFont('helvetica', 'normal');
      
      // Row 1 - Left: Faculty
      pageDoc.text('Faculty:', infoLeftColX + 2, infoTableStartY + 5);
      pageDoc.text(facultyName, infoLeftColX + 30, infoTableStartY + 5);
      
      // Row 2 - Left: Department
      pageDoc.text('Department:', infoLeftColX + 2, infoTableStartY + infoRowHeight + 5);
      pageDoc.text(departmentName, infoLeftColX + 30, infoTableStartY + infoRowHeight + 5);
      
      // Row 3 - Left: Batch
      pageDoc.text('Batch:', infoLeftColX + 2, infoTableStartY + infoRowHeight * 2 + 5);
      pageDoc.text(batchName, infoLeftColX + 30, infoTableStartY + infoRowHeight * 2 + 5);
      
      // Right side - Beautified format (same as Exam Result Sheet)
      const rightPadding = 8;
      const rightContentX = infoRightColX + rightPadding;
      
      pageDoc.setFontSize(10);
      pageDoc.setFont('helvetica', 'normal');
      
      // Row 1 - Right: Lecturer Name with underline
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text('Lecturer Name:', rightContentX, infoTableStartY + infoRowHeight + 5);
      // Draw underline for lecturer name
      const underlineStartX = rightContentX + 23;
      const underlineY = infoTableStartY + infoRowHeight + 6;
      const underlineLength = 50;
      pageDoc.setLineWidth(0.3);
      pageDoc.setDrawColor(0, 0, 0);
      pageDoc.line(underlineStartX, underlineY, underlineStartX + underlineLength, underlineY);
      
      // Row 3 - Right: Date (bold)
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text('Date:', rightContentX, infoTableStartY + infoRowHeight * 2 + 5);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text(currentDate, rightContentX + 23, infoTableStartY + infoRowHeight * 2 + 5);
    };
    
    // Let autoTable paginate one continuous data set. Manual chunking plus
    // autoTable pagination caused orphan rows and skipped student numbers.
    const studentTableStartY = infoTableStartY + infoTableHeight + 10;
    const bottomMargin = 15;
    const headerRowHeight = 8;
    const availableWidth = landscapePageWidth - (landscapeMargin * 2);
    const sNumWidth = 15;
    const studentIdWidth = 35;
    const studentNameWidth = 80;
    const dateColumnsCount = 22;
    const dateColumnWidth = (availableWidth - sNumWidth - studentIdWidth - studentNameWidth) / dateColumnsCount;
    const dateHeaders = Array(dateColumnsCount).fill('');
    const tableHeaders = ['S #', 'StudentID', 'Student Name', ...dateHeaders];
    const columnStyles = {
      0: { cellWidth: sNumWidth },
      1: { cellWidth: studentIdWidth },
      2: { cellWidth: studentNameWidth }
    };
    for (let i = 3; i < 3 + dateColumnsCount; i += 1) {
      columnStyles[i] = { cellWidth: dateColumnWidth };
    }

    const studentTableData = students.map((student, index) => [
      index + 1,
      student.student_no || '',
      student.name || student.student_name || '',
      ...Array(dateColumnsCount).fill('')
    ]);

    drawHeader(doc);
    autoTable(doc, {
      startY: studentTableStartY,
      head: [tableHeaders],
      body: studentTableData,
      theme: 'grid',
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 2,
        minCellHeight: headerRowHeight,
        lineWidth: 0.1
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 1.5,
        minCellHeight: 5.5,
        lineWidth: 0.1,
        valign: 'middle'
      },
      columnStyles,
      margin: {
        top: 8,
        left: landscapeMargin,
        right: landscapeMargin,
        bottom: bottomMargin
      },
      styles: {
        overflow: 'linebreak',
        lineWidth: 0.1,
        fontSize: 9
      }
    });

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Add footer
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    // Download PDF
    const fileName = `Attendance_Sheet_${facultyName}_${departmentName}_${batchName}.pdf`;
    doc.save(fileName);
  };


  const allFiltersSelected = filters.faculty_id && filters.department_id && filters.batch_id;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-3">
          <MdDescription className="text-blue-500" />
          <span>Teacher Sheet Report</span>
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Filter students by Faculty, Department, Semester, Batch, and Subject
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 whitespace-pre-line">{error}</p>
          <p className="text-red-600 text-sm mt-2">
            <strong>Debug Info:</strong> Please open the browser console (F12) and check for detailed error messages.
            Look for logs starting with "Marks API" or "Error loading".
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4 sm:mb-6">
          <MdFilterList className="text-gray-600 text-lg sm:text-xl" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Faculty */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              <MdSchool className="inline mr-1" />
              Faculty <span className="text-red-500">*</span>
            </label>
            <select
              value={filters.faculty_id}
              onChange={(e) => handleFilterChange('faculty_id', e.target.value)}
              disabled={faculties.length === 1}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition"
            >
              {faculties.length === 0 ? (
                <option value="">No faculty assigned</option>
              ) : (
                <>
                  {faculties.length > 1 && <option value="">Select Faculty</option>}
                  {faculties.map(faculty => (
                    <option key={faculty.id} value={faculty.id}>
                      {faculty.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Department */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              <MdSchool className="inline mr-1" />
              Department <span className="text-red-500">*</span>
            </label>
            <select
              value={filters.department_id}
              onChange={(e) => handleFilterChange('department_id', e.target.value)}
              disabled={!filters.faculty_id}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition"
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Batch */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              <MdPeople className="inline mr-1" />
              Batch <span className="text-red-500">*</span>
            </label>
            <select
              value={filters.batch_id}
              onChange={(e) => handleFilterChange('batch_id', e.target.value)}
              disabled={!filters.department_id}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition"
            >
              <option value="">Select Batch</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.name || batch.code}
                </option>
              ))}
            </select>
          </div>

        </div>

        <div className="mt-4 sm:mt-6 flex justify-end">
          <button
            onClick={handleSearch}
            disabled={!allFiltersSelected || loading}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg"
          >
            <MdFilterList />
            <span>{loading ? 'Loading...' : 'Search Students'}</span>
          </button>
        </div>
      </div>

      {/* Student List */}
      {students.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Students List ({students.length})
            </h2>
            <div className="flex items-center space-x-3 flex-wrap">
              {/* View Toggle Buttons */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'list'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title="List View"
                >
                  <MdViewList className="text-xl" />
                </button>
                <button
                  onClick={() => setViewMode('box')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'box'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Box View"
                >
                  <MdViewModule className="text-xl" />
                </button>
              </div>
              
              <button
                onClick={handleDownloadTeacherSheet}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition font-semibold flex items-center space-x-2"
              >
                <MdDownload />
                <span className="hidden sm:inline">Download Teacher Sheet</span>
                <span className="sm:hidden">Teacher Sheet</span>
              </button>
              <button
                onClick={handleDownloadAttendanceSheet}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition font-semibold flex items-center space-x-2"
              >
                <MdDownload />
                <span className="hidden sm:inline">Download Attendance Sheet</span>
                <span className="sm:hidden">Attendance</span>
              </button>
            </div>
          </div>

          {/* List View */}
          {viewMode === 'list' && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="p-3 text-left font-semibold text-gray-800">No.</th>
                    <th className="p-3 text-left font-semibold text-gray-800">Student No.</th>
                    <th className="p-3 text-left font-semibold text-gray-800">Student Name</th>
                    <th className="p-3 text-left font-semibold text-gray-800">Student Phone Number</th>
                    <th className="p-3 text-left font-semibold text-gray-800">Email Address</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={student.id || index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 text-gray-700">{student.serial_no || (index + 1)}</td>
                      <td className="p-3 text-gray-700 font-medium">{student.student_no || 'N/A'}</td>
                      <td className="p-3 text-gray-700">{student.name || 'N/A'}</td>
                      <td className="p-3 text-gray-700">{student.phone || 'N/A'}</td>
                      <td className="p-3 text-gray-700">{student.email || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Box View */}
          {viewMode === 'box' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {students.map((student, index) => (
                <div
                  key={student.id || index}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer flex items-start gap-3"
                >
                  {/* Profile Icon - Left Side */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
                      <MdPerson className="text-gray-400 text-3xl" />
                    </div>
                  </div>
                  
                  {/* Student Info - Middle Section */}
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    {/* Name - Bold Blue */}
                    <h3 className="text-base font-bold text-blue-600 leading-tight">
                      {student.name || 'N/A'}
                    </h3>
                    
                    {/* Student ID - Green Icon */}
                    <div className="flex items-center gap-2">
                      <MdPerson className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {student.student_no || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Phone Number - Blue Icon */}
                    <div className="flex items-center gap-2">
                      <MdPhone className="text-blue-400 text-lg flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {student.phone || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Email Address - Red Icon */}
                    <div className="flex items-center gap-2">
                      <MdEmail className="text-red-500 text-lg flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {student.email || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Arrow Icon - Right Side */}
                  <div className="flex-shrink-0 flex items-center">
                    <MdArrowForward className="text-gray-400 text-2xl" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && students.length === 0 && allFiltersSelected && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <MdPeople className="text-5xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Students Found</h3>
          <p className="text-gray-500">
            No students found for the selected filters. Please try different filter combinations.
          </p>
        </div>
      )}
    </div>
  );
}

export default TeacherSheetReport;
